<template>
  <div>
    <div v-if="localCards.length" class="ap-contact-cards">
      <div
        v-for="(card, idx) in localCards"
        :key="idx"
        class="ap-contact-card"
        :class="{ 'ap-contact-card--error': cardErrors[idx] && Object.values(cardErrors[idx]).some(Boolean) }"
      >
        <div class="ap-contact-card-top">
          <select-input
            v-model="card.label"
            label="Category"
            :options="labelOptions"
            :required="!!(cardErrors[idx] && cardErrors[idx].label)"
            placeholder="Select category…"
            style="margin-bottom:0"
          />
          <button
            v-if="localCards.length > minCards"
            class="ap-contact-card-remove"
            type="button"
            title="Remove"
            @click="removeCard(idx)"
          >
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>
        <select-input
          v-model="card.title"
          label="Title"
          :options="titleOptions"
          :clearable="true"
          placeholder="Select title…"
          style="margin-bottom:0"
        />
        <text-input
          v-model="card.name"
          label="Name"
          :icon="['fas', 'user']"
          :required="true"
          :uppercase="true"
          placeholder="Name"
          style="margin-bottom:0"
        />
        <phone-input
          v-model="card.phone"
          label="Phone"
          :required="!!(cardErrors[idx] && cardErrors[idx].phone)"
          style="margin-bottom:0"
        />
        <email-input
          v-model="card.email"
          label="Email"
          :required="card.roles.includes('pic') || !!(cardErrors[idx] && cardErrors[idx].email)"
          placeholder="email@example.com"
          style="margin-bottom:0"
        />
        <div class="ap-field" style="margin-bottom:0">
          <label>Email Group <span v-if="cardErrors[idx] && cardErrors[idx].emailGroup" class="ap-req">*</span></label>
          <div class="ap-contact-roles">
            <label
              v-for="role in ROLES"
              :key="role"
              class="ap-contact-role"
              :class="{
                'ap-contact-role--pic': role === 'pic',
                'is-active': card.roles.includes(role) && role !== 'pic',
                'is-active-pic': card.roles.includes(role) && role === 'pic',
                'is-disabled': isRoleDisabled(idx, role)
              }"
            >
              <input
                type="checkbox"
                :checked="card.roles.includes(role)"
                style="display:none"
                @change="toggleRole(idx, role)"
              />
              {{ role.toUpperCase() }}
            </label>
          </div>
        </div>
        <p v-if="cardErrors[idx] && cardErrors[idx].message" class="ap-card-err">
          <fa-icon :icon="['fas', 'exclamation-circle']" /> {{ cardErrors[idx].message }}
        </p>
      </div>
    </div>

    <div class="ap-contact-add-wrapper">
      <button class="ap-contact-add-btn" type="button" @click="addCard">
        <fa-icon :icon="['fas', 'plus']" /> Add Contact
      </button>
    </div>

    <div v-if="picConfirm" class="ap-confirm-overlay" @click.self="cancelPicMove">
      <div class="ap-confirm-dialog">
        <div class="ap-confirm-icon"><fa-icon :icon="['fas', 'user-tie']" /></div>
        <div class="ap-confirm-title">Change PIC?</div>
        <div class="ap-confirm-body">
          <strong>{{ picConfirm.fromName }}</strong> is currently the PIC.<br>
          Move PIC to <strong>{{ picConfirm.toName }}</strong>?
        </div>
        <div class="ap-confirm-actions">
          <button class="ap-confirm-btn ap-confirm-btn--cancel" type="button" @click="cancelPicMove">Cancel</button>
          <button class="ap-confirm-btn ap-confirm-btn--ok" type="button" @click="confirmPicMove">Yes, Change PIC</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectInput from '@/components/form/SelectInput.vue'
import TextInput from '@/components/form/TextInput.vue'
import PhoneInput from '@/components/form/PhoneInput.vue'
import EmailInput from '@/components/form/EmailInput.vue'

const LABEL_OPTIONS = [
  { value: 'Internal Staff', label: 'Internal Staff' },
  { value: 'Outsourced Account', label: 'Outsourced Account' },
  { value: 'Director', label: 'Director' },
  { value: 'Shareholder', label: 'Shareholder' }
]

const TITLE_OPTIONS = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: "Dato'", label: "Dato'" },
  { value: 'Datin', label: 'Datin' },
  { value: 'Tan Sri', label: 'Tan Sri' },
  { value: 'Puan Sri', label: 'Puan Sri' }
]

const ROLES = ['pic', 'to', 'cc', 'bcc', 'director', 'shareholder']

export default {
  name: 'ContactCards',
  components: { SelectInput, TextInput, PhoneInput, EmailInput },
  props: {
    value: { type: Array, default: () => [] },
    minCards: { type: Number, default: 0 },
    labelOptions: { type: Array, default: () => LABEL_OPTIONS },
    titleOptions: { type: Array, default: () => TITLE_OPTIONS },
    cardErrors: { type: Array, default: () => [] }
  },
  data () {
    return {
      ROLES,
      localCards: [],
      syncing: false,
      picConfirm: null
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (val) {
        const incoming = JSON.stringify(val || [])
        if (incoming !== JSON.stringify(this.localCards)) {
          this.syncing = true
          this.localCards = JSON.parse(incoming)
          this.$nextTick(() => { this.syncing = false })
        }
      }
    },
    localCards: {
      deep: true,
      handler (val) {
        if (!this.syncing) {
          this.syncing = true
          const nonPicRoles = ['to', 'cc', 'bcc', 'director', 'shareholder']
          val.forEach(card => {
            if (!card.roles.includes('pic')) {
              const email = (card.email || '').trim()
              const emailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
              if (!emailValid) {
                nonPicRoles.forEach(role => {
                  const pos = card.roles.indexOf(role)
                  if (pos > -1) card.roles.splice(pos, 1)
                })
              }
            }
          })
          this.$nextTick(() => { this.syncing = false })
          this.$emit('input', JSON.parse(JSON.stringify(val)))
        }
      }
    }
  },
  methods: {
    addCard () {
      this.localCards.push({ label: '', title: '', name: '', phone: '', email: '', roles: [] })
    },
    removeCard (idx) {
      this.localCards.splice(idx, 1)
    },
    isRoleDisabled (cardIdx, role) {
      if (role === 'pic') return false
      const card = this.localCards[cardIdx]
      if (card.roles.includes('pic')) return false
      const email = (card.email || '').trim()
      return !(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    },
    toggleRole (cardIdx, role) {
      const card = this.localCards[cardIdx]
      if (role === 'pic' && !card.roles.includes('pic')) {
        const currentPicIdx = this.localCards.findIndex((c, i) => i !== cardIdx && c.roles.includes('pic'))
        if (currentPicIdx > -1) {
          this.picConfirm = {
            fromIdx: currentPicIdx,
            fromName: this.localCards[currentPicIdx].name || `Card ${currentPicIdx + 1}`,
            toIdx: cardIdx,
            toName: card.name || `Card ${cardIdx + 1}`
          }
          return
        }
      }
      this.applyToggle(cardIdx, role)
    },
    applyToggle (cardIdx, role) {
      const card = this.localCards[cardIdx]
      if (role === 'pic') {
        this.localCards.forEach((c, i) => {
          if (i !== cardIdx) {
            const pos = c.roles.indexOf('pic')
            if (pos > -1) c.roles.splice(pos, 1)
          }
        })
      }
      const pos = card.roles.indexOf(role)
      if (pos > -1) card.roles.splice(pos, 1)
      else card.roles.push(role)
    },
    confirmPicMove () {
      const { toIdx } = this.picConfirm
      this.picConfirm = null
      this.applyToggle(toIdx, 'pic')
    },
    cancelPicMove () {
      this.picConfirm = null
    },
    scrollToFirstError () {
      const cards = this.$el.querySelectorAll('.ap-contact-card')
      const errIdx = this.cardErrors.findIndex(
        e => e && (e.label || e.name || e.phone || e.email || e.emailGroup || e.message)
      )
      if (errIdx >= 0 && cards[errIdx]) {
        cards[errIdx].scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }
}
</script>

<style scoped>
.ap-contact-add-wrapper {
  margin-top: 8px;
}

.ap-contact-add-btn {
  width: 100%;
  background: #f8fafc;
  border: 1px dashed #9ca3af;
  color: #64748b;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.ap-contact-add-btn:hover {
  border-color: #b8860b;
  color: #b8860b;
  background: #fdf8ee;
}

.ap-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.ap-confirm-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px 24px;
  max-width: 380px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  text-align: center;
}

.ap-confirm-icon {
  font-size: 28px;
  color: #b8860b;
  margin-bottom: 10px;
}

.ap-confirm-title {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.ap-confirm-body {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 22px;
}

.ap-confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.ap-confirm-btn {
  padding: 9px 22px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}

.ap-confirm-btn--cancel {
  background: #f1f5f9;
  color: #475569;
}

.ap-confirm-btn--cancel:hover {
  background: #e2e8f0;
}

.ap-confirm-btn--ok {
  background: #b8860b;
  color: #fff;
}

.ap-confirm-btn--ok:hover {
  background: #9a7009;
}

.ap-contact-role.is-disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.ap-card-err {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 5px;
}
</style>
